import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { logger } from "@/lib/logger.server";
import { INSURANCES } from "@/data/heallexa";

/**
 * The insurance catalog: carriers and the plans each one offers. Feeds BOTH
 * pickers — the doctor's "which plans do I accept" multi-select and the
 * patient's carrier -> plan search filter — so the two sides can never drift
 * onto different vocabularies.
 *
 * Tokens, and why plans are flat strings
 * --------------------------------------
 * `providers.insurance` is text[] with a GIN index, and all four search
 * functions already filter with `_insurance = ANY (p.insurance)`. A plan id
 * embeds its carrier ("aetna:choice-pos-ii"), so plan-level filtering needs no
 * join table and no change to the search SQL. A provider row carries tokens at
 * both levels:
 *
 *     {'aetna', 'aetna:choice-pos-ii', 'selfpay'}
 *
 * The doctor picker ALWAYS writes the carrier token alongside any plan token,
 * so a carrier-level search still finds doctors who only listed specific plans.
 * See aws-migration/sql/2026-07-24-insurance-catalog.sql.
 *
 * 'selfpay' is a reserved token, not a carrier row — it is the id already used
 * by INSURANCES in src/data/heallexa.ts, so it is already present in
 * providers.insurance today.
 */

export const SELF_PAY_ID = "selfpay";

export type CatalogCarrier = {
  id: string;
  name: string;
  short: string;
  color: string;
  category: "commercial" | "medicare" | "medicaid" | "military";
};

export type CatalogPlan = {
  id: string;
  carrier_id: string;
  name: string;
  plan_type: string | null;
  metal_level: string | null;
};

export type InsuranceCatalog = {
  carriers: CatalogCarrier[];
  plans: CatalogPlan[];
};

/**
 * Fallback when insurance_carriers has not been seeded yet (fresh database, or
 * the migration has not been applied to this environment). The pickers keep
 * working at carrier level off the hardcoded list rather than rendering empty.
 * 'selfpay' is filtered out — it is offered as its own checkbox, not a carrier.
 */
function fallbackCarriers(): CatalogCarrier[] {
  return INSURANCES.filter((i) => i.id !== SELF_PAY_ID).map((i) => ({
    id: i.id,
    name: i.name,
    short: i.short,
    color: i.color,
    category: "commercial" as const,
  }));
}

// ponytail: module-level cache per warm Lambda container. The catalog changes
// when an admin approves a plan — minutes-stale is fine, and this runs on every
// search page load. Matches the pattern in search-providers.functions.ts.
let _cache: { data: InsuranceCatalog; expires: number } | undefined;
let _loggedFailure = false;
const CACHE_TTL_MS = 300_000;

export const listInsuranceCatalogFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<InsuranceCatalog> => {
    if (_cache && _cache.expires > Date.now()) return _cache.data;

    try {
      const carriers = await db.query<CatalogCarrier>(
        `SELECT id, name, short, color, category
           FROM public.insurance_carriers
          WHERE is_active
          ORDER BY popularity DESC, name ASC`,
      );

      // Unapproved plans are provider-submitted and awaiting admin review; they
      // must not appear in either picker until promoted.
      const plans = await db.query<CatalogPlan>(
        `SELECT id, carrier_id, name, plan_type, metal_level
           FROM public.insurance_plans
          WHERE is_approved
          ORDER BY carrier_id ASC, name ASC`,
      );

      const data: InsuranceCatalog = {
        carriers: carriers.length ? carriers : fallbackCarriers(),
        plans,
      };
      _cache = { data, expires: Date.now() + CACHE_TTL_MS };
      return data;
    } catch (err) {
      // A missing table (migration not applied to this environment) must not
      // break search or the doctor's profile form.
      //
      // Cache the fallback too, and log at most once per container: this runs
      // on every search page load, so logging each failure buries the real
      // errors under thousands of identical stacks (observed in dev before the
      // migration was applied). 42P01 = undefined_table, the expected
      // "migration not applied yet" case; anything else is a genuine fault and
      // stays at error level.
      const fallback = { carriers: fallbackCarriers(), plans: [] };
      if (!_loggedFailure) {
        _loggedFailure = true;
        const code = (err as { code?: string })?.code;
        if (code === "42P01") {
          logger.warn(
            "[insurance-catalog] insurance_carriers/insurance_plans missing — " +
              "serving static carriers. Apply aws-migration/sql/2026-07-24-insurance-catalog.sql.",
          );
        } else {
          logger.error("[insurance-catalog] query failed, serving static carriers", err);
        }
      }
      _cache = { data: fallback, expires: Date.now() + CACHE_TTL_MS };
      return fallback;
    }
  },
);

/**
 * Trim a CMS plan name down to what is worth reading in a list.
 *
 * CMS marketing names are correct but verbose, and inside a carrier's own plan
 * list the leading brand is pure repetition — under the "Blue Cross Blue Shield"
 * heading, every row starting "Anthem Blue Cross and Blue Shield …" wastes the
 * width that distinguishes one plan from the next. The metal level and plan type
 * are rendered as separate chips, so strip those too when they merely repeat.
 *
 * Deliberately conservative: it only removes a LEADING carrier brand and
 * trailing type/metal repetition. The cost-sharing digits ("5000/10%/8000" —
 * deductible / coinsurance / out-of-pocket max) stay, because that is exactly
 * what tells two sibling plans apart.
 *
 * Never returns empty: if stripping would leave nothing, the original name wins.
 */
export function cleanPlanName(
  name: string,
  carrier?: { name?: string; short?: string } | null,
  planType?: string | null,
  metalLevel?: string | null,
): string {
  let out = name.trim();

  // Longest brand alias first, so "Anthem Blue Cross and Blue Shield" is removed
  // rather than just the "Anthem" prefix inside it.
  const aliases = [carrier?.name, carrier?.short]
    .filter((v): v is string => Boolean(v && v.trim()))
    .sort((a, b) => b.length - a.length);

  for (const alias of aliases) {
    // Tolerate "and"/"&" differing between the carrier record and the plan name.
    const pattern = alias
      .trim()
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\s+(and|&)\s+/gi, "\\s+(?:and|&)\\s+")
      .replace(/\s+/g, "\\s+");
    const stripped = out.replace(new RegExp(`^${pattern}\\s+`, "i"), "").trim();
    if (stripped) {
      out = stripped;
      break;
    }
  }

  // "… EPO (EPO)" / trailing metal word — both are shown as chips.
  for (const token of [planType, metalLevel]) {
    if (!token?.trim()) continue;
    const t = token.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const stripped = out.replace(new RegExp(`[\\s(]*\\b${t}\\b\\)?\\s*$`, "i"), "").trim();
    if (stripped) out = stripped;
  }

  return out.replace(/\s{2,}/g, " ").trim() || name.trim();
}

/**
 * In-network status for the insurance the patient filtered on.
 *
 * Mirrors public.insurance_rank() in
 * aws-migration/sql/2026-07-26-insurance-rank-not-filter.sql, which does the
 * ordering server-side. Both must agree or a card will claim one thing while
 * sitting in another tier's position.
 *
 * The `unknown` tier is the common case, not an edge case: most of the 6.18M
 * imported listings have an empty insurance[] because nobody has filled it in.
 * Saying "out-of-network" there would be a false negative about a real practice,
 * so it gets an explicit "call to confirm" instead.
 */
export function insuranceStatusFor(
  providerInsurance: string[] | null | undefined,
  token: string | null,
): "in" | "out" | "unknown" | null {
  if (!token) return null;
  const list = providerInsurance ?? [];
  if (list.length === 0) return "unknown";
  return list.includes(token) ? "in" : "out";
}

/**
 * Expand a picker selection into the token array stored in providers.insurance.
 *
 * Selecting a plan implies its carrier: a doctor who lists only
 * "aetna:choice-pos-ii" must still be found by a patient who filtered on plain
 * "aetna". Without this the carrier-level search — the one most patients use —
 * would silently miss them.
 *
 * Pure and exported so both the doctor form and the admin listing form use the
 * same rule. Tested in insurance-catalog.test.ts.
 */
export function expandInsuranceTokens(selected: string[]): string[] {
  const out = new Set<string>();
  for (const token of selected) {
    const t = token.trim();
    if (!t) continue;
    out.add(t);
    const colon = t.indexOf(":");
    if (colon > 0) out.add(t.slice(0, colon));
  }
  return [...out];
}
